import {
  PayLaterOption,
  TdsConfig,
  MarketingConfig,
  RestaurantInfo
} from '../../types/config'
import { Cart } from '../../types/cart'
import { CartPaymentType } from '../../types/orders'
import { EventType } from '../../types/form'
import { getCheckTotal } from '../../utils/cart-helpers'
import { useRestaurant } from '@local/do-secundo-restaurant-provider'
import { useFlags } from '@local/do-secundo-feature-flags'
import { getInitialPaymentType, PaymentValidationMethod } from './utils'
import { useGetCart } from '../CartQuery/CartQuery'
import { useCartValidator } from '../Cart/Validation/CartValidation'

export interface CheckoutProps {
  cart: Cart | undefined
  cartValid: boolean
  isPayLaterRequired: boolean
  showPaymentToggle: boolean
  showPaymentInformation: boolean
  showSmsMarketingCheckbox: boolean
  // Restaurant and config data needed by CheckoutForm
  isIntlRx: boolean
  isLoading: boolean // Combined loading state for checkout data
  isError: boolean
  eventType?: EventType
  payLaterOption?: PayLaterOption
  tdsConfig?: TdsConfig
  marketingConfig?: MarketingConfig
  // Payment configuration
  initialPaymentType: CartPaymentType
  paymentValidationMethod: PaymentValidationMethod
  // Only include restaurantInfo and spiEnabled for DomesticCheckoutFormBody
  restaurantInfo?: RestaurantInfo
  spiEnabled: boolean
}

const getPaymentValidationMethod = (
  isIntlRx: boolean,
  spiEnabled: boolean
): PaymentValidationMethod => {
  if (isIntlRx) return 'ADYEN'
  if (spiEnabled) return 'SPI'
  return 'LEGACY'
}

/**
 * Encapsulates checkout business logic and state management for all checkout components.
 * Provides restaurant configuration, payment settings, and checkout eligibility rules.
 * Centralizes data fetching and configuration logic to reduce duplication.
 */
export const useCheckoutProps = (): CheckoutProps => {
  // Get cart data
  const { cart } = useGetCart()
  const { cartValid } = useCartValidator(cart)

  const { nvsCooSpi: spiEnabled } = useFlags()
  const {
    isIntlRx,
    applicableConfigs,
    ooConfig,
    restaurantInfo,
    isLoading,
    isError
  } = useRestaurant()
  const {
    payLaterOption,
    tdsConfig,
    taxExemptEnabled,
    marketingConfig,
    eventType
  } = applicableConfigs
  const promoCodesEnabled = ooConfig?.promoCodesEnabled

  const cartHasBalance = cart ? getCheckTotal(cart) > 0 : false

  // TDS (Third-party delivery service) constraints
  const payLaterBlockedByTds = tdsConfig?.enabled

  // Pay later availability logic
  const payLaterEligible = cartHasBalance && !payLaterBlockedByTds
  const isPayLaterAllowed =
    payLaterOption === PayLaterOption.ALLOW && payLaterEligible
  const isPayLaterRequired =
    payLaterOption === PayLaterOption.REQUIRE && payLaterEligible
  const isPayLaterAvailable = isPayLaterAllowed || isPayLaterRequired

  // UI element visibility
  const showPaymentToggle = isPayLaterAllowed // Only show toggle when pay later is optional

  // Payment information section visibility
  const showPaymentInformation =
    !isIntlRx || promoCodesEnabled || taxExemptEnabled || isPayLaterAvailable

  // Marketing checkbox visibility
  const showSmsMarketingCheckbox = marketingConfig?.smsMarketingEnabled ?? false

  // Calculate initial payment type
  const initialPaymentType = cart
    ? getInitialPaymentType(tdsConfig, payLaterOption, cart)
    : 'CREDIT'

  // Determine payment validation method
  const paymentValidationMethod = getPaymentValidationMethod(
    isIntlRx,
    spiEnabled
  )

  // Combined loading state that includes:
  // - Restaurant data loading
  // - Cart not yet loaded
  // - EventType not available during loading
  const isCheckoutDataLoading = isLoading || !cart || (!eventType && isLoading)

  return {
    cart,
    cartValid,
    isPayLaterRequired,
    showPaymentToggle,
    showPaymentInformation,
    showSmsMarketingCheckbox,
    // Restaurant and config data needed by CheckoutForm
    isIntlRx,
    isLoading: isCheckoutDataLoading,
    isError,
    eventType,
    payLaterOption,
    tdsConfig,
    marketingConfig,
    // Payment configuration
    initialPaymentType,
    paymentValidationMethod,
    // Only include restaurantInfo and spiEnabled for DomesticCheckoutFormBody
    restaurantInfo,
    spiEnabled
  }
}
